import React from 'react';

import { useInView } from 'react-intersection-observer';

import { Image } from '@change/ds-core-ui';

import { LazyComponent } from '#src/app/shared/components/layout/lazy';

import type { VideoComponentProps } from '../VideoComponent';

/**
 * Lazily loads `VideoComponent` in its own chunk, so that @mux/mux-player-react (only reachable
 * through it) stays out of the petitionDetails initial bundle and is fetched on demand when
 * a card scrolls into view. The dynamic import must wrap the whole VideoComponent — not just
 * VideoPlayer — because the petitionVideo barrel is not tree-shaken (no `sideEffects`
 * flag), so any static import from it would drag mux back into the initial graph.
 */
const loadVideoComponent = async (): Promise<React.ComponentType<VideoComponentProps>> =>
	(await import(/* webpackChunkName: "pages/petitionDetails-supporterVoicesVideo" */ '../VideoComponent'))
		.VideoComponent;

/**
 * Defers mounting of the (Mux-backed) video component until it scrolls near the viewport,
 * showing a lightweight poster placeholder until then. Shared by the legacy videos section
 * and the mixed supporter voices feed so both keep Mux out of the initial bundle.
 */
export const LazyVideoComponent: React.FC<VideoComponentProps & { poster: string | undefined }> = ({
	poster,
	...videoProps
}) => {
	const { ref, inView } = useInView({
		triggerOnce: true,
		rootMargin: '0px 200px',
	});

	const placeholder = (
		<div className="flex h-[320px] items-center justify-center">
			{poster ? (
				<Image src={poster} className="rounded-large aspect-auto h-full" aria-hidden />
			) : (
				<div className="rounded-large bg-background-raised h-full w-[180px]" />
			)}
		</div>
	);

	if (!inView) {
		return <div ref={ref}>{placeholder}</div>;
	}

	return (
		<LazyComponent
			loadComponent={loadVideoComponent}
			fallback={placeholder}
			/* oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions */
			{...(videoProps as any)}
		/>
	);
};
